import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { renderToString } from 'react-dom/server';
import Highcharts from 'highcharts';
import sunburstModule from 'highcharts/modules/sunburst';
import HighchartsReact from 'highcharts-react-official';
import ActionBar from './ActionBar';
import SunburstTooltip from './SunburstTooltip';
import { ANOMALY_EVENTS, CHART_TYPE, DIMENSION_SEPARATOR, trackAnomalyEvent } from '../../consts';
import { computeSubtreeColors, restoreOriginalColors } from '../../queryUtils';
import css from './SunburstChart.css';

sunburstModule(Highcharts);

const SELECTED_CLASS = 'sunburst-selected';

const SunburstChart = ({
    data,
    metricLabels,
    displayFormats,
    increasesAreGood,
    onRunRca,
    firstPeriod,
    secondPeriod,
    translate,
}) => {
    const selectedRef = useRef(null);
    const actionBarRef = useRef(null);
    const tooltipCacheRef = useRef(new Map());
    const wrapperRef = useRef(null);
    const chartComponentRef = useRef(null);

    // Highcharts only reflows on window resize; when the chart is revealed after being
    // display:none (RCA round-trip) the container size changes without one.
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => {
            chartComponentRef.current?.chart?.reflow();
        });
        observer.observe(wrapper);
        return () => observer.disconnect();
    }, []);

    useEffect(() => () => tooltipCacheRef.current.clear(), [
        data,
        metricLabels,
        displayFormats,
        firstPeriod,
        secondPeriod,
        translate,
    ]);

    const updateSelection = useCallback((pointData, graphic) => {
        const prevEl = document.querySelector(`.${SELECTED_CLASS}`);
        if (prevEl) prevEl.classList.remove(SELECTED_CLASS);
        if (graphic) graphic.element.classList.add(SELECTED_CLASS);
        selectedRef.current = pointData;
        if (pointData) {
            trackAnomalyEvent(ANOMALY_EVENTS.SEGMENT_SELECTED, {
                depth: pointData.id.split(DIMENSION_SEPARATOR).length,
            });
            actionBarRef.current.show(pointData);
        } else {
            actionBarRef.current.hide();
        }
    }, []);

    const dismissSelection = useCallback(() => {
        updateSelection(null, null);
    }, [updateSelection]);

    const handlePointClick = useCallback(
        function handleClick() {
            const point = this;
            if (point.id === selectedRef.current?.id) {
                dismissSelection();
            } else {
                updateSelection(
                    {
                        id: point.id,
                        name: point.name,
                        custom: point.custom,
                    },
                    point.graphic
                );
            }
            return true;
        },
        [updateSelection, dismissSelection]
    );

    const handleRcaClick = useCallback(
        selected => {
            onRunRca?.(selected);
            dismissSelection();
        },
        [onRunRca, dismissSelection]
    );

    const handleSetRootNode = useCallback(
        function onSetRootNode(event) {
            dismissSelection();
            const { chart } = this;
            const { newRootId } = event;
            trackAnomalyEvent(ANOMALY_EVENTS.CHART_ZOOM, {
                isReset: !newRootId,
                depth: newRootId ? newRootId.split(DIMENSION_SEPARATOR).length : 0,
            });
            const updates = newRootId
                ? computeSubtreeColors(chart, newRootId, increasesAreGood)
                : restoreOriginalColors(chart);
            updates.forEach(({ point, color }) => point.update({ color }, false));
            chart.redraw();
        },
        [dismissSelection, increasesAreGood]
    );

    const options = useMemo(() => {
        return {
            chart: {
                type: CHART_TYPE,
                animation: false,
                spacingTop: 30,
            },
            title: { text: '' },
            credits: { enabled: false },
            exporting: { enabled: false },
            plotOptions: {
                series: {
                    animation: false,
                    point: {
                        events: {
                            click: handlePointClick,
                        },
                    },
                },
            },
            series: [
                {
                    data,
                    events: {
                        setRootNode: handleSetRootNode,
                    },
                    allowTraversingTree: true,
                    levelIsConstant: false,
                    borderWidth: 2,
                    borderColor: '#FFFFFF',
                    cursor: 'pointer',
                    colorByPoint: false,
                    traverseUpButton: {
                        text: '← Back',
                        relativeTo: 'spacingBox',
                        position: { align: 'left', x: -2, y: -10 },
                        theme: {
                            fill: 'none',
                            stroke: 'none',
                            'stroke-width': 0,
                            style: {
                                color: '#3A92E5',
                                fontSize: '13px',
                            },
                            states: {
                                hover: { fill: 'none', style: { color: '#3074B7' } },
                            },
                        },
                    },
                    dataLabels: {
                        format: '{point.name}',
                        rotationMode: 'circular',
                        style: {
                            fontSize: '11px',
                            fontWeight: '500',
                            textOutline: 'none',
                            textShadow: 'none',
                            color: '#000000',
                        },
                    },
                    states: {
                        hover: {
                            brightness: 0.05,
                            halo: false,
                            borderColor: '#FFFFFF',
                            borderWidth: 0,
                            shadow: false,
                        },
                        inactive: {
                            enabled: false,
                        },
                    },
                },
            ],
            colorAxis: { min: -1, max: 1, showInLegend: false },
            tooltip: {
                useHTML: true,
                style: { pointerEvents: 'none' },
                borderColor: '#e0e0e0',
                borderWidth: 1,
                borderRadius: 8,
                shadow: false,
                hideDelay: 0,
                animation: false,
                formatter() {
                    const cache = tooltipCacheRef.current;
                    const cached = cache.get(this.point.id);
                    if (cached) return cached;
                    const path = this.point.id.split(DIMENSION_SEPARATOR).join(' > ');
                    const html = renderToString(
                        <SunburstTooltip
                            translate={translate}
                            path={path}
                            firstPeriod={firstPeriod}
                            secondPeriod={secondPeriod}
                            metricLabels={metricLabels}
                            displayFormats={displayFormats}
                            custom={this.point.custom}
                        />
                    );
                    cache.set(this.point.id, html);
                    return html;
                },
            },
        };
    }, [data, metricLabels, displayFormats, firstPeriod, secondPeriod, handlePointClick, handleSetRootNode, translate]);

    return (
        <div ref={wrapperRef} className={css.chartWrapper}>
            <HighchartsReact
                ref={chartComponentRef}
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { height: '100%', width: '100%' } }}
            />
            <ActionBar ref={actionBarRef} onRunRca={handleRcaClick} onDismiss={dismissSelection} />
        </div>
    );
};

SunburstChart.propTypes = {
    data: PropTypes.array,
    metricLabels: PropTypes.object,
    displayFormats: PropTypes.object,
    increasesAreGood: PropTypes.bool,
    onRunRca: PropTypes.func,
    firstPeriod: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }),
    secondPeriod: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }),
    translate: PropTypes.func.isRequired,
};

export default withLocalize(SunburstChart);
